import { useState, useEffect } from 'react'
import api from '../../../services/api'
import { Plus, Edit, Power, Loader2, X, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import ConfirmModal from '../../../components/ConfirmModal'

export default function ListSettings({ type, title }) {
    const [list, setList] = useState([])
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)
    const [editingItem, setEditingItem] = useState(null)
    const [modalLoading, setModalLoading] = useState(false)
    const [formData, setFormData] = useState({ nom: '', code: '' })

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        item: null,
        loading: false
    })

    useEffect(() => {
        fetchList()
    }, [type])

    const fetchList = async () => {
        setLoading(true)
        try {
            const res = await api.get(`/settings/${type}`)
            setList(res.data.data.list)
        } catch (err) {
            toast.error('Erreur de chargement')
        } finally {
            setLoading(false)
        }
    }

    const openAddModal = () => {
        setEditingItem(null)
        setFormData({ nom: '', code: '' })
        setShowModal(true)
    }

    const openEditModal = (item) => {
        setEditingItem(item)
        setFormData({ nom: item.nom, code: item.code })
        setShowModal(true)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setModalLoading(true)
        try {
            if (editingItem) {
                await api.put(`/settings/${type}/${editingItem._id}`, formData)
            } else {
                await api.post(`/settings/${type}`, formData)
            }
            toast.success('Enregistré avec succès')
            setShowModal(false)
            fetchList()
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement')
        } finally {
            setModalLoading(false)
        }
    }

    const handleToggleStatus = (item) => {
        setConfirmModal({ open: true, item, loading: false })
    }

    const confirmToggle = async () => {
        const { item } = confirmModal
        if (!item) return

        setConfirmModal(prev => ({ ...prev, loading: true }))
        try {
            const newStatus = item.etat === 'ACTIF' ? 'SUPPRIME' : 'ACTIF'
            await api.put(`/settings/${type}/${item._id}`, { etat: newStatus })
            toast.success('Statut mis à jour')
            setConfirmModal({ open: false, item: null, loading: false })
            fetchList()
        } catch (err) {
            toast.error('Erreur lors de la modification')
            setConfirmModal(prev => ({ ...prev, loading: false }))
        }
    }

    return (
        <div>
            <div className="settings-header-actions">
                <h3 className="section-title">{title}</h3>
                <button className="btn-primary flex items-center gap-2" onClick={openAddModal}>
                    <Plus size={16} /> Ajouter
                </button>
            </div>

            {loading ? (
                <div className="p-5 text-center"><Loader2 className="animate-spin inline" /></div>
            ) : (
                <table className="settings-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Code</th>
                            <th>Statut</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.length === 0 ? (
                            <tr><td colSpan={4} className="text-center p-4">Aucune donnée</td></tr>
                        ) : (
                            list.map(item => (
                                <tr key={item._id} style={{ opacity: item.etat === 'SUPPRIME' ? 0.6 : 1 }}>
                                    <td>{item.nom}</td>
                                    <td><span className="badge badge-neutral bg-gray-100 text-gray-800">{item.code}</span></td>
                                    <td>
                                        <span className={`badge ${item.etat === 'ACTIF' ? 'badge-success' : 'badge-error'}`}>
                                            {item.etat === 'ACTIF' ? 'Actif' : 'Désactivé'}
                                        </span>
                                    </td>
                                    <td>
                                        <button className="action-btn" onClick={() => openEditModal(item)}>
                                            <Edit size={16} />
                                        </button>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleToggleStatus(item)}
                                            title={item.etat === 'ACTIF' ? 'Désactiver' : 'Activer'}
                                        >
                                            <Power size={16} color={item.etat === 'ACTIF' ? 'red' : 'green'} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            )}

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
                    <div className="modal bg-white rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold">{editingItem ? 'Modifier' : 'Ajouter'}</h3>
                            <button onClick={() => setShowModal(false)}><X size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label">Nom *</label>
                                <input
                                    className="form-input w-full"
                                    value={formData.nom}
                                    onChange={e => setFormData({ ...formData, nom: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Code *</label>
                                <input
                                    className="form-input w-full"
                                    value={formData.code}
                                    onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                    required
                                />
                            </div>
                            <div className="flex justify-end gap-2 mt-4">
                                <button type="button" className="px-4 py-2 border rounded" onClick={() => setShowModal(false)}>Annuler</button>
                                <button type="submit" className="btn-primary flex items-center gap-2" disabled={modalLoading}>
                                    {modalLoading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                    Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModal.open}
                onClose={() => setConfirmModal({ open: false, item: null })}
                onConfirm={confirmToggle}
                title="Changer statut"
                message={`Voulez-vous vraiment ${confirmModal.item?.etat === 'ACTIF' ? 'désactiver' : 'activer'} "${confirmModal.item?.nom}" ?`}
                type="warning"
                loading={confirmModal.loading}
            />
        </div>
    )
}
